import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { companySpacesQueryOptions } from "./queries";
import type { CompanySpace } from "./types";

function SpaceCard({ space }: { space: CompanySpace }) {
  const navigate = useNavigate();
  const clickable = space.is_member;
  const open = () => {
    if (!clickable) return;
    navigate({ to: "/spaces/$id", params: { id: space.id } });
  };

  return (
    <div
      className={`company-space-card${clickable ? "" : " company-space-card--locked"}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      aria-disabled={clickable ? undefined : true}
      title={clickable ? undefined : "Вы не участник этого пространства"}
      onClick={clickable ? open : undefined}
      onKeyDown={(e) => {
        if (clickable && e.key === "Enter") open();
      }}
    >
      <header className="company-space-head">
        <h3 className="company-space-name">{space.name}</h3>
        <div className="company-space-counters" aria-label="Сводка задач">
          <span
            className="company-space-counter company-space-counter--warn"
            title="Требуют внимания"
          >
            ⚠ {space.attention_count}
          </span>
          <span
            className="company-space-counter company-space-counter--active"
            title="Сейчас в работе"
          >
            ▶ {space.in_progress_count}
          </span>
          <span
            className="company-space-counter company-space-counter--ok"
            title="Завершено за 24 часа"
          >
            ✓ {space.completed_24h_count}
          </span>
        </div>
      </header>
      <div className="company-space-agents">
        <span className="company-space-agents-label">Агенты</span>
        {space.agents.length === 0 ? (
          <span className="company-space-agents-empty">—</span>
        ) : (
          <div className="company-space-agent-chips">
            {space.agents.slice(0, 3).map((a) => (
              <span key={a.id} className="company-agent-chip" title={a.label}>
                {a.emoji && (
                  <span className="company-agent-emoji" aria-hidden>
                    {a.emoji}
                  </span>
                )}
                <span>{a.label}</span>
              </span>
            ))}
            {space.agents.length > 3 && (
              <span className="company-agent-chip company-agent-chip--more">
                +{space.agents.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CompanySpacesGrid() {
  const { data = [], isLoading } = useQuery(companySpacesQueryOptions());

  return (
    <section>
      <h2 className="company-section-title">
        Пространства
        <span className="company-count-chip">{data.length}</span>
      </h2>
      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : data.length === 0 ? (
        <div className="cmd-empty">
          <p className="cmd-empty-title">Пространств ещё нет</p>
          <p className="cmd-empty-hint">
            Создайте первое командное пространство, чтобы группировать задачи.
          </p>
        </div>
      ) : (
        <div className="company-spaces-grid">
          {data.map((s) => (
            <SpaceCard key={s.id} space={s} />
          ))}
        </div>
      )}
    </section>
  );
}
